import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { CommonArea } from '../types/commonArea';
import { commonAreaService } from '../services/commonAreaService';
import PhotoGallery from './PhotoGallery';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import api from '../services/api';
import '../styles/ModelDetailsSlider.css';

interface CommonAreaDetailsSliderProps {
  workspaceId: string;
  commonAreaId: string;
  onClose: () => void;
}

const CommonAreaDetailsSlider: React.FC<CommonAreaDetailsSliderProps> = ({
  workspaceId,
  commonAreaId,
  onClose
}) => {
  const [commonArea, setCommonArea] = useState<CommonArea | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editModeBasicInfo, setEditModeBasicInfo] = useState(false);
  const [levels, setLevels] = useState<string[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [basicInfoFormData, setBasicInfoFormData] = useState<{
    areaName?: string;
    level?: string;
    area?: number;
    buildingType?: string;
    areaType?: 'AMENITY' | 'UTILITY' | 'CIRCULATION' | 'PARKING' | 'STORAGE' | 'OTHER';
    status?: 'ACTIVE' | 'UNDER_CONSTRUCTION' | 'PLANNED' | 'INACTIVE';
    description?: string;
    capacity?: number;
    operatingHours?: string;
    isAccessible?: boolean;
    requiresKeyAccess?: boolean;
  }>({});

  useEffect(() => {
    fetchCommonArea();
  }, [commonAreaId]);

  // Fetch levels when entering edit mode
  useEffect(() => {
    if (editModeBasicInfo && commonArea?.projectId) {
      fetchLevels(commonArea.projectId);
    }
  }, [editModeBasicInfo, commonArea?.projectId]);

  const fetchLevels = async (projectId: string) => {
    if (!projectId) {
      setLevels([]);
      return;
    }

    try {
      setLoadingLevels(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas/by-project/${projectId}/levels`);
      const levelsList = response.data.levels || [];
      setLevels(levelsList);
    } catch (error) {
      console.error('Error fetching levels:', error);
      toast.error('Failed to load levels');
      setLevels([]);
    } finally {
      setLoadingLevels(false);
    }
  };

  const fetchCommonArea = async () => {
    try {
      setLoading(true);
      const response = await commonAreaService.getById(workspaceId, commonAreaId);
      setCommonArea(response.commonArea);
      // Initialize form data
      setBasicInfoFormData({
        areaName: response.commonArea.areaName,
        level: response.commonArea.level,
        area: response.commonArea.area,
        buildingType: response.commonArea.buildingType,
        areaType: response.commonArea.areaType,
        status: response.commonArea.status,
        description: response.commonArea.description,
        capacity: response.commonArea.capacity,
        operatingHours: response.commonArea.operatingHours,
        isAccessible: response.commonArea.isAccessible,
        requiresKeyAccess: response.commonArea.requiresKeyAccess
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch common area details');
      console.error('Error fetching common area:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoEdit = () => {
    if (commonArea) {
      setBasicInfoFormData({
        areaName: commonArea.areaName,
        level: commonArea.level,
        area: commonArea.area,
        buildingType: commonArea.buildingType,
        areaType: commonArea.areaType,
        status: commonArea.status,
        description: commonArea.description,
        capacity: commonArea.capacity,
        operatingHours: commonArea.operatingHours,
        isAccessible: commonArea.isAccessible,
        requiresKeyAccess: commonArea.requiresKeyAccess
      });
    }
    setEditModeBasicInfo(true);
  };

  const handleBasicInfoSave = async () => {
    try {
      setSaving(true);
      await commonAreaService.update(workspaceId, commonAreaId, basicInfoFormData);
      await fetchCommonArea();
      setEditModeBasicInfo(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update common area information');
    } finally {
      setSaving(false);
    }
  };

  const handleBasicInfoCancel = () => {
    setEditModeBasicInfo(false);
  };

  const handleDelete = async () => {
    try {
      await commonAreaService.delete(workspaceId, commonAreaId);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete common area');
    }
  };

  if (loading) {
    return (
      <div className="model-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !commonArea) {
    return (
      <div className="model-details-slider">
        <div className="model-details-error">
          <p>{error || 'Common area not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>{commonArea.areaName}</h2>
            <p>Project: {commonArea.projectName || '-'} | Level: {commonArea.level || '-'}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="model-details-content">
        {/* Left Column */}
        <div className="model-details-left">
          {/* Basic Information Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Basic Information</h3>
              {!editModeBasicInfo && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleBasicInfoEdit}
                  title="Edit basic information"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editModeBasicInfo ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Area Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoFormData.areaName || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, areaName: e.target.value })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Level</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.level || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, level: e.target.value })}
                        disabled={loadingLevels}
                      >
                        <option value="">
                          {loadingLevels ? 'Loading levels...' : 'Select a level'}
                        </option>
                        {levels.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                        {/* Show current level if it's not in the list */}
                        {basicInfoFormData.level && !levels.includes(basicInfoFormData.level) && (
                          <option value={basicInfoFormData.level}>
                            {basicInfoFormData.level} (current)
                          </option>
                        )}
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoFormData.area || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, area: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Building Type</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.buildingType || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, buildingType: e.target.value || undefined })}
                      >
                        <option value="">Select Building Type</option>
                        <option value="Condominium">Condominium</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Detached">Detached</option>
                        <option value="Semi-Detached">Semi-Detached</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Area Type</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.areaType || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setBasicInfoFormData({
                            ...basicInfoFormData,
                            areaType: value ? value as 'AMENITY' | 'UTILITY' | 'CIRCULATION' | 'PARKING' | 'STORAGE' | 'OTHER' : undefined
                          });
                        }}
                      >
                        <option value="">Select Type</option>
                        <option value="AMENITY">Amenity</option>
                        <option value="UTILITY">Utility</option>
                        <option value="CIRCULATION">Circulation</option>
                        <option value="PARKING">Parking</option>
                        <option value="STORAGE">Storage</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Status</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.status || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setBasicInfoFormData({
                            ...basicInfoFormData,
                            status: value ? value as 'ACTIVE' | 'UNDER_CONSTRUCTION' | 'PLANNED' | 'INACTIVE' : undefined
                          });
                        }}
                      >
                        <option value="">Select Status</option>
                        <option value="ACTIVE">Active</option>
                        <option value="UNDER_CONSTRUCTION">Under Construction</option>
                        <option value="PLANNED">Planned</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Capacity</label>
                      <input
                        type="number"
                        className="form-input"
                        value={basicInfoFormData.capacity || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, capacity: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Operating Hours</label>
                      <input
                        type="text"
                        className="form-input"
                        value={basicInfoFormData.operatingHours || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, operatingHours: e.target.value })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Accessible</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.isAccessible ? 'true' : 'false'}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, isAccessible: e.target.value === 'true' })}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Requires Key Access</label>
                      <select
                        className="form-input"
                        value={basicInfoFormData.requiresKeyAccess ? 'true' : 'false'}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, requiresKeyAccess: e.target.value === 'true' })}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="model-details-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Description</label>
                      <textarea
                        className="form-input"
                        value={basicInfoFormData.description || ''}
                        onChange={(e) => setBasicInfoFormData({ ...basicInfoFormData, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleBasicInfoCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleBasicInfoSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Area Name</div>
                  <div className="model-detail-value">{commonArea.areaName}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Level</div>
                  <div className="model-detail-value">{commonArea.level || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Area (Sq.Ft)</div>
                  <div className="model-detail-value">{commonArea.area || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Building Type</div>
                  <div className="model-detail-value">{commonArea.buildingType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Area Type</div>
                  <div className="model-detail-value">{commonArea.areaType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Status</div>
                  <div className="model-detail-value">
                    <span className={`status-badge badge-${commonArea.status?.toLowerCase()}`}>
                      {commonArea.status}
                    </span>
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Capacity</div>
                  <div className="model-detail-value">{commonArea.capacity || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Operating Hours</div>
                  <div className="model-detail-value">{commonArea.operatingHours || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Accessible</div>
                  <div className="model-detail-value">{commonArea.isAccessible ? 'Yes' : 'No'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Requires Key Access</div>
                  <div className="model-detail-value">{commonArea.requiresKeyAccess ? 'Yes' : 'No'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Description</div>
                  <div className="model-detail-value">{commonArea.description || '-'}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="model-details-right">
          {/* Floor Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="common_area"
                entityId={commonAreaId}
                category="floor_layout"
              />
            </div>
          </div>

          {/* Electrical Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Electrical Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="common_area"
                entityId={commonAreaId}
                category="electrical_layout"
              />
            </div>
          </div>

          {/* Photo Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Photo Gallery</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="common_area"
                entityId={commonAreaId}
                category="photo_gallery"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="model-details-footer">
        <button
          className="btn btn-danger btn-sm"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Common Area
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="model-details-modal-overlay">
          <div className="model-details-modal">
            <h3>Delete Common Area</h3>
            <p>
              Are you sure you want to delete <strong>{commonArea.areaName}</strong>? This action cannot be undone.
            </p>
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-md"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change History FAB Button */}
      <ChangeHistoryButton
        onClick={() => setHistoryDrawerOpen(true)}
        isOpen={historyDrawerOpen}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        workspaceId={workspaceId}
        entityType="common_area"
        entityId={commonAreaId}
      />
    </div>
  );
};

export default CommonAreaDetailsSlider;
